/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Heading } from '@chakra-ui/layout'
import { Box, Button, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { ErrorResponseDTO } from 'models'
import { errosInstitution } from 'pages/Institution/utils/errosInstitution'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { createUser, editUser } from 'services/users'
import { InputFormUser } from './form'
import { FormSchemaUser, FormUserProps } from './interfaces'
import { Schema } from './schema'

const FormUser: React.FC<FormUserProps> = ({
    data,
    idUser,
    perfis,
    onSubmitForm,
    onCancelForm
}) => {
    const toast = useToast()
    const { currentInstitution, getInstitutionUser } = useInstitutionsContext()

    const { isBackoffice } = useAuthContext()

    const form = useMemo(
        () =>
            InputFormUser(
                idUser ?? '',
                perfis.filter(data => data.perfil !== 'Usuário Admin da IF'),
                isBackoffice
            ),
        [idUser, perfis]
    )

    const defaultValue: FormSchemaUser = {
        tipoUsuarioPerfil: data?.tipoUsuarioPerfil.id || '',
        nome: data?.nome || '',
        cpf: data?.cpf || '',
        grupo: '',
        nomeUsuario: data?.credencial.nomeUsuario || '',
        contato: data?.telefone || '',
        email: data?.email || '',
        matricula: data?.matricula || '',
        alocacao: data?.alocacao || '',
        utilizaMfa: data?.utilizaMfa || false
    }

    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting },
        watch
    } = useForm<FormSchemaUser>({
        resolver: yupResolver(Schema(idUser)),
        mode: 'onChange',
        defaultValues: idUser ? defaultValue : {}
    })

    const disableFields = (field: string): boolean => {
        if (!idUser || !field) return false
        return ['cpf', 'email'].includes(field)
    }

    function emitToastSuccess() {
        toast({
            title: 'Usuário',
            description: 'Usuário editado com sucesso!',
            position: 'bottom-left',
            status: 'success',
            isClosable: true
        })
    }

    function emitToastError(error: any) {
        const message =
            error?.response?.data[0]?.mensagem ??
            'Algo deu errado, tente novamente mais tarde.'

        toast({
            title: `Falha ao Salvar Usuário`,
            description: message,
            position: 'bottom-left',
            status: 'error',
            isClosable: true
        })
    }

    async function fetchEditUser(data: FormSchemaUser) {
        try {
            const params = (data: FormSchemaUser) => {
                const {
                    nome,
                    contato,
                    tipoUsuarioPerfil,
                    matricula,
                    alocacao,
                    nomeUsuario,
                    utilizaMfa
                } = data
                return {
                    nome,
                    telefone: contato.replace(/\D/g, ''),
                    matricula,
                    tipoUsuarioPerfil,
                    username: nomeUsuario,
                    alocacao,
                    utilizaMfa: utilizaMfa === 'true'
                }
            }

            await editUser(params(data), idUser)
            emitToastSuccess()
            onSubmitForm && onSubmitForm()
        } catch (error) {
            emitToastError(error)
        }
    }

    const buildPayloadToCreate = (data: FormSchemaUser) => {
        const {
            nome,
            cpf,
            email,
            contato,
            tipoUsuarioPerfil,
            matricula,
            alocacao
        } = data
        const typeUser = perfis.find(
            perfil => perfil.id === tipoUsuarioPerfil
        )?.perfil
        const rest: any = {
            nome,
            cpf: cpf.replace(/\D/g, ''),
            tipoUsuarioPerfil,
            tipo: typeUser,
            credencial: {
                nomeUsuario: email
            },
            telefone: contato.replace(/\D/g, ''),
            email: email,
            matricula,
            alocacao,
            utilizaMfa: data.utilizaMfa === 'true'
        }

        if (typeUser !== 'backoffice' && typeUser !== 'detran') {
            return {
                ...rest,
                instituicaoFinanceira: {
                    guid: currentInstitution?.id
                }
            }
        }

        return rest
    }

    const fetchCreateUsers = async (data: FormSchemaUser) => {
        try {
            await createUser(buildPayloadToCreate(data))
            onSubmitForm && onSubmitForm()
        } catch (e: any) {
            if (e.response?.status !== 500) {
                const message: any =
                    e.response?.data[0]?.mensagem ??
                    'Algo deu errado, tente novamente mais tarde.'
                toast({
                    title: 'Erro ao cadastrar usuário',
                    description: message,
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true,
                    variant: 'solid-error'
                })
            } else {
                toast({
                    title: 'Erro ao cadastrar usuário',
                    description: 'Algo deu errado, tente novamente mais tarde.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true,
                    variant: 'solid-error'
                })
            }
        }
    }

    const onSubmit = async (value: FormSchemaUser) => {
        idUser ? await fetchEditUser(value) : await fetchCreateUsers(value)
        onCancel()
    }

    const onCancel = (): void => {
        onCancelForm && onCancelForm()
    }

    useEffect(() => {
        if (!currentInstitution) {
            toast({
                title: 'Instituição Financeira não selecionada!',
                description:
                    'Selecione uma Instituição Financeira e tente novamente.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            onCancel()
        }
    }, [currentInstitution])

    const fetchUsersByIdUser = async () => {
        currentInstitution &&
            idUser &&
            getInstitutionUser(
                currentInstitution.id,
                idUser,
                isBackoffice
            ).catch(({ response }) => {
                const { data } = response as ErrorResponseDTO
                data.filter(
                    (elem, index, arr) =>
                        index ===
                        arr.findIndex(
                            checkEle => checkEle.codigo === elem.codigo
                        )
                ).map(res => {
                    const error = errosInstitution[res.codigo]
                    toast({
                        title: error.title,
                        description: error.description,
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true,
                        variant: 'solid-error'
                    })
                    onCancel()
                })
            })
    }
    useEffect(() => {
        fetchUsersByIdUser()
    }, [idUser])

    return (
        <Box overflowY="scroll" maxH="550px">
            <form onSubmit={handleSubmit(onSubmit)} data-testid="form">
                <Layout
                    type="container"
                    px={8}
                    full
                    display="grid"
                    gap={8}
                    data-testid="form-container"
                >
                    <Layout type="row" data-testid="form-heading">
                        <Heading size="md" textAlign={{ sm: 'center' }}>
                            {!idUser ? 'Novo usuário' : 'Editar usuário'}
                        </Heading>
                    </Layout>
                    <Layout
                        type="row"
                        gutter={{ y: 1.5 }}
                        data-testid="form-fields"
                    >
                        {form.map(({ layout, input, select }, key) => {
                            if (!idUser && input && input.id === 'nomeUsuario')
                                return
                            return (
                                <Layout {...layout} key={key}>
                                    {select ? (
                                        <SelectBuild
                                            {...select}
                                            err={errors[select.id] as any}
                                            {...register(select.id)}
                                        />
                                    ) : (
                                        input && (
                                            <InputBuild
                                                {...input}
                                                err={errors[input.id] as any}
                                                {...register(input.id)}
                                                value={watch(input.id)}
                                                isDisable={disableFields(
                                                    input.id
                                                )}
                                            />
                                        )
                                    )}
                                </Layout>
                            )
                        })}
                    </Layout>
                    <Layout type="row" data-testid="form-action">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={6}
                        >
                            <Button
                                data-testid="form-action-cancel"
                                colorScheme="primary"
                                variant="outline"
                                onClick={onCancel}
                            >
                                Cancelar
                            </Button>
                            <Button
                                data-testid="form-action-confirm"
                                colorScheme="primary"
                                type="submit"
                                isLoading={isSubmitting}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Layout>
                </Layout>
            </form>
        </Box>
    )
}

export default FormUser
